// Enterprise API service for new features
import api from './api';

// Departments API
export const departmentApi = {
  getAll: () => api.get('/departments'),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  addModule: (id, moduleData) => api.post(`/departments/${id}/modules`, moduleData),
  removeModule: (id, moduleId) => api.delete(`/departments/${id}/modules/${moduleId}`),
};

// Teams API
export const teamApi = {
  getAll: () => api.get('/teams'),
  getById: (id) => api.get(`/teams/${id}`),
  getByDepartment: (deptId) => api.get(`/teams/department/${deptId}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  addMember: (id, memberData) => api.post(`/teams/${id}/members`, memberData),
  removeMember: (id, userId) => api.delete(`/teams/${id}/members/${userId}`),
};

// Branches API
export const branchApi = {
  getAll: () => api.get('/branches'),
  getTree: () => api.get('/branches/tree'),
  getById: (id) => api.get(`/branches/${id}`),
  getHierarchy: (id) => api.get(`/branches/${id}/hierarchy`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  getStats: (id) => api.get(`/branches/${id}/stats`),
  updateStats: (id, stats) => api.patch(`/branches/${id}/stats`, stats),
  delete: (id) => api.delete(`/branches/${id}`),
};

// Goals API
export const goalApi = {
  getAll: (params) => api.get('/goals', { params }),
  getMyGoals: (params) => api.get('/goals/my-goals', { params }),
  getTeamGoals: (teamId, params) => api.get(`/goals/team/${teamId}`, { params }),
  getById: (id) => api.get(`/goals/${id}`),
  getHierarchy: (id) => api.get(`/goals/${id}/hierarchy`),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  updateProgress: (id, data) => api.patch(`/goals/${id}/progress`, data),
  cascade: (id, assignments) => api.post(`/goals/${id}/cascade`, { childAssignments: assignments }),
  delete: (id) => api.delete(`/goals/${id}`),
};

// Activities API
export const activityApi = {
  getAll: (params) => api.get('/activities', { params }),
  getMyActivities: (params) => api.get('/activities/my-activities', { params }),
  getUserStats: (userId, params) => api.get(`/activities/stats/user/${userId}`, { params }),
  getMyStats: (params) => api.get('/activities/stats/my-stats', { params }),
  getBreakdown: (userId, params) => api.get(`/activities/stats/breakdown/${userId}`, { params }),
  getTeamStats: (teamId, params) => api.get(`/activities/stats/team/${teamId}`, { params }),
  getBattleCard: (params) => api.get('/activities/stats/battle-card', { params }),
  getScores: () => api.get('/activities/scores'),
  getById: (id) => api.get(`/activities/${id}`),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
};

// Forecasts API
export const forecastApi = {
  getAll: (params) => api.get('/forecasts', { params }),
  getMyForecasts: (params) => api.get('/forecasts/my-forecasts', { params }),
  getById: (id) => api.get(`/forecasts/${id}`),
  create: (data) => api.post('/forecasts', data),
  calculate: (data) => api.post('/forecasts/calculate', data),
  update: (id, data) => api.put(`/forecasts/${id}`, data),
  updateActual: (id, actual) => api.patch(`/forecasts/${id}/actual`, { actual }),
  generateInsights: (id) => api.post(`/forecasts/${id}/insights`),
  delete: (id) => api.delete(`/forecasts/${id}`),
};

// Intelligence API
export const intelligenceApi = {
  getDashboard: (params) => api.get('/intelligence/dashboard', { params }),
  getStaleClients: (params) => api.get('/intelligence/stale-clients', { params }),
  getStuckDeals: (params) => api.get('/intelligence/stuck-deals', { params }),
  getGoalPredictions: (params) => api.get('/intelligence/goal-predictions', { params }),
  getOverdueFollowups: (params) => api.get('/intelligence/overdue-followups', { params }),
  getDealsClosingSoon: (params) => api.get('/intelligence/deals-closing-soon', { params }),
  getLowActivityUsers: (params) => api.get('/intelligence/low-activity-users', { params }),
};

// Workflows API
export const workflowApi = {
  getAll: (params) => api.get('/workflows', { params }),
  getTemplates: (params) => api.get('/workflows/templates', { params }),
  getById: (id) => api.get(`/workflows/${id}`),
  getExecutions: (id, params) => api.get(`/workflows/${id}/executions`, { params }),
  getStats: (id, params) => api.get(`/workflows/${id}/stats`, { params }),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  clone: (id, name) => api.post(`/workflows/${id}/clone`, { name }),
  execute: (id, data) => api.post(`/workflows/${id}/execute`, data),
  toggle: (id) => api.patch(`/workflows/${id}/toggle`),
  delete: (id) => api.delete(`/workflows/${id}`),
};

// Comments API
export const commentApi = {
  getEntityComments: (entityType, entityId, params) => 
    api.get(`/comments/entity/${entityType}/${entityId}`, { params }),
  getThread: (id) => api.get(`/comments/${id}/thread`),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.put(`/comments/${id}`, data),
  addReaction: (id, emoji) => api.post(`/comments/${id}/reactions`, { emoji }),
  removeReaction: (id, emoji) => api.delete(`/comments/${id}/reactions/${emoji}`),
  pin: (id) => api.patch(`/comments/${id}/pin`),
  delete: (id) => api.delete(`/comments/${id}`),
};

// Notes API
export const noteApi = {
  getAll: (params) => api.get('/notes', { params }),
  getMyNotes: (params) => api.get('/notes/my-notes', { params }),
  getFavorites: () => api.get('/notes/favorites'),
  getById: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data) => api.put(`/notes/${id}`, data),
  addCollaborator: (id, data) => api.post(`/notes/${id}/collaborators`, data),
  toggleFavorite: (id) => api.patch(`/notes/${id}/favorite`),
  delete: (id) => api.delete(`/notes/${id}`),
};

// Custom Reports API
export const customReportApi = {
  getAll: (params) => api.get('/reports-custom', { params }),
  getTemplates: (params) => api.get('/reports-custom/templates', { params }),
  getById: (id) => api.get(`/reports-custom/${id}`),
  create: (data) => api.post('/reports-custom', data),
  update: (id, data) => api.put(`/reports-custom/${id}`, data),
  execute: (id) => api.post(`/reports-custom/${id}/execute`),
  clone: (id, name) => api.post(`/reports-custom/${id}/clone`, { name }),
  delete: (id) => api.delete(`/reports-custom/${id}`),
};

// Pipelines API
export const pipelineApi = {
  getAll: (params) => api.get('/pipelines', { params }),
  getDefault: (entityType) => api.get(`/pipelines/default/${entityType}`),
  getById: (id) => api.get(`/pipelines/${id}`),
  create: (data) => api.post('/pipelines', data),
  update: (id, data) => api.put(`/pipelines/${id}`, data),
  addStage: (id, stage) => api.post(`/pipelines/${id}/stages`, stage),
  removeStage: (id, stageName) => api.delete(`/pipelines/${id}/stages/${stageName}`),
  reorderStages: (id, stageOrder) => api.put(`/pipelines/${id}/stages/reorder`, { stageOrder }),
  createDefault: () => api.post('/pipelines/setup/default'),
  delete: (id) => api.delete(`/pipelines/${id}`),
};

// Custom Fields API
export const customFieldApi = {
  getAll: (params) => api.get('/custom-fields', { params }),
  getByEntity: (entityType) => api.get(`/custom-fields/entity/${entityType}`),
  getById: (id) => api.get(`/custom-fields/${id}`),
  create: (data) => api.post('/custom-fields', data),
  update: (id, data) => api.put(`/custom-fields/${id}`, data),
  validate: (entityType, data) => api.post(`/custom-fields/validate/${entityType}`, data),
  reorder: (entityType, order) => api.put(`/custom-fields/reorder/${entityType}`, { fieldOrder: order }),
  delete: (id) => api.delete(`/custom-fields/${id}`),
};

// Predictive Analytics API
export const predictiveAnalyticsApi = {
  getSalesForecast: (params) => api.get('/predictive-analytics/sales-forecast', { params }),
  getLeadScoring: () => api.get('/predictive-analytics/lead-scoring'),
  getPerformancePrediction: (agentId) => api.get(`/predictive-analytics/performance-prediction/${agentId}`),
  getChurnPrediction: () => api.get('/predictive-analytics/churn-prediction'),
};

// Setup API
export const setupAPI = {
  getIndustryTemplates: () => api.get('/setup/industry-templates'),
  getIndustryTemplate: (industryId) => api.get(`/setup/industry-templates/${industryId}`),
  initializeOrganization: (data) => api.post('/setup/initialize-organization', data),
  getStatus: () => api.get('/setup/status'),
};

// Metrics API - Cascading metrics system
export const metricsAPI = {
  getMyMetrics: (params) => api.get('/metrics/my-metrics', { params }),
  getUserMetrics: (userId, params) => api.get(`/metrics/user/${userId}`, { params }),
  getTeamMetrics: (teamId, params) => api.get(`/metrics/team/${teamId}`, { params }),
  getDepartmentMetrics: (departmentId, params) => api.get(`/metrics/department/${departmentId}`, { params }),
  getCompanyMetrics: (params) => api.get('/metrics/company', { params }),
  getHierarchyMetrics: (params) => api.get('/metrics/hierarchy', { params }),
  getCascadeMetrics: (level, id, params) => api.get(`/metrics/cascade/${level}/${id}`, { params }),
};

export default {
  departments: departmentApi,
  teams: teamApi,
  branches: branchApi,
  goals: goalApi,
  activities: activityApi,
  forecasts: forecastApi,
  intelligence: intelligenceApi,
  workflows: workflowApi,
  comments: commentApi,
  notes: noteApi,
  customReports: customReportApi,
  pipelines: pipelineApi,
  customFields: customFieldApi,
  predictiveAnalytics: predictiveAnalyticsApi,
  setup: setupAPI,
  metrics: metricsAPI,
};

// Messages API
export const messagesAPI = {
  // Get all conversations for current user
  getConversations: () => api.get('/messages/conversations'),
  
  // Get messages in a specific conversation
  getConversation: (conversationId, params) => api.get(`/messages/conversation/${conversationId}`, { params }),
  
  // Send a message
  send: (data) => api.post('/messages/send', data),
  
  // Send message to entire team
  sendToTeam: (teamId, data) => api.post(`/messages/team/${teamId}`, data),
  
  // Send message to entire department
  sendToDepartment: (departmentId, data) => api.post(`/messages/department/${departmentId}`, data),
  
  // Send message to entire branch
  sendToBranch: (branchId, data) => api.post(`/messages/branch/${branchId}`, data),
  
  // Create or get direct conversation with a user
  createDirect: (recipientId) => api.post(`/messages/direct/${recipientId}`),
  
  // Mark message as read
  markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),
  
  // Get unread message count
  getUnreadCount: () => api.get('/messages/unread-count'),
  
  // Delete a message (soft delete)
  delete: (messageId) => api.delete(`/messages/${messageId}`)
};
